import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

import { getProductionHandover } from '../../../../api/productionHandoverApi';

import {
  productionHandoverEventLabels,
  productionHandoverIssueLabels,
  productionHandoverStatusLabels,
} from '../../../../utils/productionHandoverLabels';
import { SOCKET_URL } from '../../../../config';
import { io } from 'socket.io-client';

const socket = io(SOCKET_URL);

const getEventColor = (eventType) => {
  switch (eventType) {
    case 'FINALIZED_COMPLETED':
      return 'success';

    case 'FINALIZED_DISCREPANCY':
    case 'FAILED':
      return 'error';

    case 'CLOSED_WITH_DISCREPANCY':
      return 'warning';

    case 'PROCESSING_STARTED':
    case 'DATA_COLLECTED':
      return 'info';

    case 'REQUESTED':
    case 'RETRIED_AFTER_FAILURE':
    case 'REVIEW_RETRY_REQUESTED':
      return 'primary';

    default:
      return 'default';
  }
};

const getActorLabel = (event) => {
  if (event.actorName) {
    return event.actorRole
      ? `${event.actorName} (${event.actorRole})`
      : event.actorName;
  }

  if (event.actorEmail) {
    return event.actorEmail;
  }

  switch (event.actorType) {
    case 'BOT':
      return 'Bot AMBF';

    case 'SYSTEM':
      return 'System';

    case 'USER':
      return 'Użytkownik';

    default:
      return '-';
  }
};

const getStatusLabel = (status) => {
  if (!status) {
    return '-';
  }

  return productionHandoverStatusLabels[status] || status;
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  return new Intl.DateTimeFormat('pl-PL', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(date);
};

const formatNumber = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }

  return new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 3,
  }).format(value);
};

const formatBoolean = (value) => {
  if (value === true) {
    return 'Tak';
  }

  if (value === false) {
    return 'Nie';
  }

  return '-';
};

const getStatusChipColor = (status) => {
  switch (status) {
    case 'AVAILABLE':
      return 'default';

    case 'REQUESTED':
      return 'info';

    case 'PROCESSING':
      return 'primary';

    case 'DATA_COLLECTED':
      return 'warning';

    case 'COMPLETED':
      return 'success';

    case 'DISCREPANCY':
      return 'error';

    case 'FAILED':
      return 'error';

    case 'CLOSED_WITH_DISCREPANCY':
      return 'warning';

    default:
      return 'default';
  }
};

const getErrorMessage = (error, fallbackMessage) => {
  const responseMessage = error?.response?.data?.message;

  if (Array.isArray(responseMessage)) {
    return responseMessage.join(', ');
  }

  if (responseMessage) {
    return responseMessage;
  }

  return fallbackMessage;
};

const InfoField = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>

    <Typography
      variant="body2"
      sx={{
        fontWeight: 500,
        overflowWrap: 'anywhere',
      }}
    >
      {value ?? '-'}
    </Typography>
  </Box>
);

const SummaryTile = ({ label, value, secondaryValue }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1.5,
      minHeight: 88,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>

    <Typography
      variant="h6"
      sx={{
        mt: 0.25,
        fontWeight: 600,
      }}
    >
      {formatNumber(value)}
    </Typography>

    {secondaryValue !== undefined && (
      <Typography variant="caption" color="text.secondary">
        Ilość: {formatNumber(secondaryValue)}
      </Typography>
    )}
  </Paper>
);

const ProductionHandoverEventItem = ({ event }) => {
  const metadata =
    event?.metadata && typeof event.metadata === 'object'
      ? event.metadata
      : null;

  const summary =
    metadata?.summary && typeof metadata.summary === 'object'
      ? metadata.summary
      : null;

  const snapshotIssues = Array.isArray(metadata?.issues) ? metadata.issues : [];

  const hasSnapshot = Boolean(summary) || snapshotIssues.length > 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.5,
      }}
    >
      <Stack
        direction={{
          xs: 'column',
          sm: 'row',
        }}
        justifyContent="space-between"
        alignItems={{
          xs: 'flex-start',
          sm: 'center',
        }}
        spacing={1}
      >
        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              color={getEventColor(event.eventType)}
              label={
                productionHandoverEventLabels[event.eventType] ||
                event.eventType
              }
            />

            {event.previousStatus && event.newStatus && (
              <Typography variant="caption" color="text.secondary">
                {getStatusLabel(event.previousStatus)}
                {' → '}
                {getStatusLabel(event.newStatus)}
              </Typography>
            )}
          </Stack>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.75 }}
          >
            {formatDate(event.createdAt)}
            {' • '}
            {getActorLabel(event)}
          </Typography>
        </Box>
      </Stack>

      {event.comment && (
        <Alert
          severity={
            event.eventType === 'CLOSED_WITH_DISCREPANCY' ? 'warning' : 'info'
          }
          sx={{
            mt: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {event.comment}
        </Alert>
      )}

      {hasSnapshot && (
        <Accordion
          disableGutters
          elevation={0}
          sx={{
            mt: 1.5,
            border: 1,
            borderColor: 'divider',
            '&:before': {
              display: 'none',
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Pokaż zapis wyniku analizy</Typography>
          </AccordionSummary>

          <AccordionDetails>
            {summary && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(4, 1fr)',
                  },
                  gap: 1.5,
                  mb: snapshotIssues.length > 0 ? 2 : 0,
                }}
              >
                <InfoField
                  label="HU produkcyjne"
                  value={formatNumber(summary.productionHuCount)}
                />

                <InfoField
                  label="Ilość produkcyjna"
                  value={formatNumber(summary.productionQuantity)}
                />

                <InfoField
                  label="HU potwierdzone EWM"
                  value={formatNumber(summary.ewmConfirmedHuCount)}
                />

                <InfoField
                  label="Ilość EWM"
                  value={formatNumber(summary.ewmConfirmedQuantity)}
                />

                <InfoField
                  label="HU w trakcie"
                  value={formatNumber(summary.ewmInProcessHuCount)}
                />

                <InfoField
                  label="Niezgodności"
                  value={formatNumber(summary.discrepancyHuCount)}
                />
              </Box>
            )}

            {snapshotIssues.length > 0 && (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>SSCC</TableCell>

                      <TableCell>Typ problemu</TableCell>

                      <TableCell align="right">Ilość produkcja</TableCell>

                      <TableCell align="right">Ilość EWM</TableCell>

                      <TableCell>Status EWM</TableCell>

                      <TableCell>Komunikat</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {snapshotIssues.map((issue, index) => (
                      <TableRow
                        key={issue.id || `${event.id}-${issue.sscc}-${index}`}
                      >
                        <TableCell>{issue.sscc || '-'}</TableCell>

                        <TableCell>
                          {productionHandoverIssueLabels[issue.issueType] ||
                            issue.issueType ||
                            '-'}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(issue.productionQuantity)}
                        </TableCell>

                        <TableCell align="right">
                          {formatNumber(issue.ewmQuantity)}
                        </TableCell>

                        <TableCell>{issue.ewmStatus || '-'}</TableCell>

                        <TableCell
                          sx={{
                            minWidth: 220,
                            whiteSpace: 'normal',
                          }}
                        >
                          {issue.errorMessage || '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>
      )}
    </Paper>
  );
};

const ProductionHandoverDetailsDialog = ({ open, handoverId, onClose }) => {
  const [handover, setHandover] = useState(null);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const loadDetails = useCallback(async () => {
    if (!handoverId) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getProductionHandover(handoverId);

      setHandover(result);
    } catch (loadError) {
      console.error(loadError);

      setError(
        getErrorMessage(
          loadError,
          'Nie udało się pobrać szczegółów przekazania produkcji.',
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [handoverId]);

  useEffect(() => {
    if (!open || !handoverId) {
      return;
    }

    loadDetails();
  }, [open, handoverId, loadDetails]);

  useEffect(() => {
    if (!open || !handoverId) {
      return undefined;
    }

    const handleChanged = (eventData) => {
      const changedId = eventData?.id;

      if (!changedId || changedId === handoverId) {
        loadDetails();
      }
    };

    socket.on('productionHandoverChanged', handleChanged);

    return () => {
      socket.off('productionHandoverChanged', handleChanged);
    };
  }, [open, handoverId, loadDetails]);

  useEffect(() => {
    if (!open) {
      setHandover(null);
      setError(null);
    }
  }, [open]);

  const issues = useMemo(
    () => (Array.isArray(handover?.issues) ? handover.issues : []),
    [handover],
  );

  const events = useMemo(
    () => (Array.isArray(handover?.events) ? handover.events : []),
    [handover],
  );

  const inProcessIssues = useMemo(
    () => issues.filter((issue) => issue.issueType === 'EWM_IN_PROCESS'),
    [issues],
  );

  const discrepancyIssues = useMemo(
    () => issues.filter((issue) => issue.issueType !== 'EWM_IN_PROCESS'),
    [issues],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
    >
      <DialogTitle
        sx={{
          pr: 12,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="h6">Szczegóły przekazania produkcji</Typography>

          {handover?.status && (
            <Chip
              size="small"
              label={
                productionHandoverStatusLabels[handover.status] ||
                handover.status
              }
              color={getStatusChipColor(handover.status)}
            />
          )}
        </Stack>

        <Stack
          direction="row"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <Tooltip title="Odśwież">
            <span>
              <IconButton
                onClick={loadDetails}
                disabled={loading || !handoverId}
              >
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Zamknij">
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {loading && !handover && (
          <Box
            sx={{
              minHeight: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {handover && (
          <Stack spacing={3}>
            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                Dane zlecenia
              </Typography>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, 1fr)',
                      md: 'repeat(4, 1fr)',
                    },
                    gap: 2,
                  }}
                >
                  <InfoField
                    label="Zlecenie produkcyjne"
                    value={handover.productionOrder}
                  />

                  <InfoField
                    label="Materiał"
                    value={handover.materialNumberSnapshot ?? '-'}
                  />

                  <InfoField
                    label="Nazwa produktu"
                    value={
                      handover.productNameSnapshot ||
                      handover.product?.name ||
                      '-'
                    }
                  />

                  <InfoField label="Partia" value={handover.batch || '-'} />

                  <InfoField
                    label="Linia produkcyjna"
                    value={handover.productionLine || '-'}
                  />

                  <InfoField
                    label="Magazyn"
                    value={handover.warehouse || '-'}
                  />

                  <InfoField
                    label="Zakład źródłowy"
                    value={handover.sourcePlant || '-'}
                  />

                  <InfoField label="Źródło" value={handover.source || '-'} />

                  <InfoField
                    label="Planowany start"
                    value={formatDate(handover.plannedStartAt)}
                  />

                  <InfoField
                    label="Data utworzenia"
                    value={formatDate(handover.createdAt)}
                  />

                  <InfoField
                    label="Data zgłoszenia"
                    value={formatDate(handover.requestedAt)}
                  />

                  <InfoField
                    label="Rozpoczęcie analizy"
                    value={formatDate(handover.processingStartedAt)}
                  />

                  <InfoField
                    label="Data wyniku"
                    value={formatDate(handover.resultGeneratedAt)}
                  />

                  <InfoField
                    label="Zebranie danych"
                    value={formatDate(handover.dataCollectedAt)}
                  />

                  <InfoField
                    label="Zakończenie"
                    value={formatDate(handover.completedAt)}
                  />
                </Box>
              </Paper>
            </Box>

            {handover.status === 'FAILED' && handover.errorMessage && (
              <Alert severity="error">
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Błąd przetwarzania
                </Typography>

                {handover.errorMessage}
              </Alert>
            )}

            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                Podsumowanie
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    md: 'repeat(4, 1fr)',
                  },
                  gap: 1.5,
                }}
              >
                <SummaryTile
                  label="HU w systemie produkcyjnym"
                  value={handover.productionHuCount}
                  secondaryValue={handover.productionQuantity}
                />

                <SummaryTile
                  label="HU potwierdzone w SAP"
                  value={handover.productionSapConfirmedHuCount}
                />

                <SummaryTile
                  label="HU bez potwierdzenia SAP"
                  value={handover.productionSapMissingHuCount}
                />

                <SummaryTile
                  label="HU potwierdzone w EWM"
                  value={handover.ewmConfirmedHuCount}
                  secondaryValue={handover.ewmConfirmedQuantity}
                />

                <SummaryTile
                  label="HU w trakcie w EWM"
                  value={handover.ewmInProcessHuCount}
                  secondaryValue={handover.ewmInProcessQuantity}
                />

                <SummaryTile
                  label="HU bez zadania EWM"
                  value={handover.missingInEwmHuCount}
                />

                <SummaryTile
                  label="HU niepotwierdzone w EWM"
                  value={handover.unconfirmedEwmHuCount}
                />

                <SummaryTile
                  label="HU z różnicą ilości"
                  value={handover.quantityMismatchHuCount}
                />

                <SummaryTile
                  label="Dodatkowe HU w EWM"
                  value={handover.extraEwmHuCount}
                />

                <SummaryTile
                  label="Łączna liczba niezgodności"
                  value={handover.discrepancyHuCount}
                />
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                HU w trakcie realizacji
              </Typography>

              {inProcessIssues.length === 0 ? (
                <Alert severity="success">Brak HU w trakcie realizacji.</Alert>
              ) : (
                <>
                  <Alert severity="info" sx={{ mb: 1.5 }}>
                    Te pozycje mają status informacyjny i nie są traktowane jako
                    niezgodność.
                  </Alert>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>SSCC</TableCell>

                          <TableCell align="right">Ilość produkcja</TableCell>

                          <TableCell align="right">Ilość EWM</TableCell>

                          <TableCell>Status EWM</TableCell>

                          <TableCell>WT</TableCell>

                          <TableCell>Proces</TableCell>

                          <TableCell>Miejsce źródłowe</TableCell>

                          <TableCell>Miejsce docelowe</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {inProcessIssues.map((issue) => (
                          <TableRow key={issue.id} hover>
                            <TableCell>{issue.sscc}</TableCell>

                            <TableCell align="right">
                              {formatNumber(issue.productionQuantity)}
                            </TableCell>

                            <TableCell align="right">
                              {formatNumber(issue.ewmQuantity)}
                            </TableCell>

                            <TableCell>{issue.ewmStatus || '-'}</TableCell>

                            <TableCell>{issue.warehouseTask || '-'}</TableCell>

                            <TableCell>{issue.processType || '-'}</TableCell>

                            <TableCell>
                              {[issue.sourceStorageType, issue.sourceBin]
                                .filter(Boolean)
                                .join(' / ') || '-'}
                            </TableCell>

                            <TableCell>
                              {[
                                issue.destinationStorageType,
                                issue.destinationBin,
                              ]
                                .filter(Boolean)
                                .join(' / ') || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>

            <Divider />

            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                Niezgodności
              </Typography>

              {discrepancyIssues.length === 0 ? (
                <Alert severity="success">Nie wykryto niezgodności.</Alert>
              ) : (
                <>
                  <Alert severity="error" sx={{ mb: 1.5 }}>
                    Liczba pozycji wymagających weryfikacji:{' '}
                    {discrepancyIssues.length}
                  </Alert>

                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>SSCC</TableCell>

                          <TableCell>Typ problemu</TableCell>

                          <TableCell align="right">Ilość produkcja</TableCell>

                          <TableCell align="right">Ilość EWM</TableCell>

                          <TableCell>SAP potwierdzony</TableCell>

                          <TableCell>Status EWM</TableCell>

                          <TableCell>WT</TableCell>

                          <TableCell>Proces</TableCell>

                          <TableCell>Miejsce źródłowe</TableCell>

                          <TableCell>Miejsce docelowe</TableCell>

                          <TableCell>Komunikat</TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {discrepancyIssues.map((issue) => (
                          <TableRow key={issue.id} hover>
                            <TableCell>{issue.sscc}</TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                color="error"
                                variant="outlined"
                                label={
                                  productionHandoverIssueLabels[
                                    issue.issueType
                                  ] || issue.issueType
                                }
                              />
                            </TableCell>

                            <TableCell align="right">
                              {formatNumber(issue.productionQuantity)}
                            </TableCell>

                            <TableCell align="right">
                              {formatNumber(issue.ewmQuantity)}
                            </TableCell>

                            <TableCell>
                              {formatBoolean(issue.productionSapConfirmed)}
                            </TableCell>

                            <TableCell>{issue.ewmStatus || '-'}</TableCell>

                            <TableCell>{issue.warehouseTask || '-'}</TableCell>

                            <TableCell>{issue.processType || '-'}</TableCell>

                            <TableCell>
                              {[issue.sourceStorageType, issue.sourceBin]
                                .filter(Boolean)
                                .join(' / ') || '-'}
                            </TableCell>

                            <TableCell>
                              {[
                                issue.destinationStorageType,
                                issue.destinationBin,
                              ]
                                .filter(Boolean)
                                .join(' / ') || '-'}
                            </TableCell>

                            <TableCell
                              sx={{
                                minWidth: 220,
                                whiteSpace: 'normal',
                              }}
                            >
                              {issue.errorMessage || '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </Box>
            <Divider />

            <Box>
              <Typography
                variant="subtitle1"
                sx={{
                  mb: 1.5,
                  fontWeight: 600,
                }}
              >
                Historia procesu
              </Typography>

              {events.length === 0 ? (
                <Alert severity="info">
                  Brak zapisanych zdarzeń dla tego przekazania.
                </Alert>
              ) : (
                <Stack spacing={1.5}>
                  {events.map((event) => (
                    <ProductionHandoverEventItem key={event.id} event={event} />
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Zamknij
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductionHandoverDetailsDialog;
